import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

import ApiService from '@/services/ApiService'
import JwtService from '@/services/JwtService'

import router from '@/router'
import RolesHelper from '@/helpers/Roles'

const DEFAULT_USER = {
    name: '',
    email: '',
    id: '',
    avatar: '',
    verified: false,
    role: '',
    roles: [],
    token: '',
    code: '',
    creator: null,
}

export const useUserStore = defineStore('user', () => {
    /** Init parameters */
    const errors = ref([])
    const user = ref(DEFAULT_USER)
    const isAuthenticated = computed(() => !!JwtService.getToken())
    const isVerified = computed(() => !!user.value.verified)
    const role = computed(() => (user.value.role ? user.value.role : null))
    const roles = computed(() => (user.value.roles ? user.value.roles : []))
    const userInStorage = localStorage.getItem('user')

    /** Methods */
    if (userInStorage) {
        const storageUser = JSON.parse(userInStorage)
        user.value = {
            ...user.value,
            ...storageUser,
        }
    }
    watch(
        () => user,
        (state) => {
            localStorage.setItem('user', JSON.stringify(state.value))
        },
        { deep: true }
    )

    /**
     * Set authenticated user
     * @param authUser
     */
    function setAuth(authUser = {}, setToken = true) {
        user.value = { ...authUser }
        errors.value = []
        user.value.roles = authUser.roles
        if (setToken) {
            JwtService.setToken(authUser.token)
        }
    }

    /**
     * Purge store
     */
    function purgeAuth() {
        user.value = DEFAULT_USER
        localStorage.removeItem('user')
        errors.value = []
        JwtService.destroyToken()
    }

    /**
     * Set errors
     * @param _errors
     */
    function setErrors(_errors = []) {
        errors.value = [..._errors]
        return errors.value
    }

    /**
     * User login
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function login(credentials) {
        return ApiService.post('login', credentials)
            .then(({ data }) => {
                setAuth(data)
                return data
            })
            .catch(({ data }) => {
                throw data
            })
    }

    /**
     * Log user out
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function logout() {
        return ApiService.get('logout')
            .then(() => {
                purgeAuth()
                router.push({ name: 'login' })
            })
            .catch(({ data }) => {
                throw data
            })
    }

    /**
     * Get information user connected
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function me() {
        return ApiService.get('me')
            .then(({ data }) => {
                // setAuth(data)
                return data
            })
            .catch(({ data }) => {
                throw data
            })
    }

    /**
     * Create a tmp account, get a code to verify email
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function register(data) {
        return ApiService.post('register', data)
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data
            })
    }

    /**
     * Register, submit the code
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function registerCode(uuid, code) {
        return ApiService.post('register/user/' + uuid + '/code', { code: code })
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data
            })
    }

    /**
     * Create an account
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function registerPassword(uuid, values) {
        return ApiService.post('register/user/' + uuid + '/password', values)
            .then(({ data }) => {
                setAuth(data)
                return data
            })
            .catch(({ data }) => {
                throw data
            })
    }

    const redirectionRoute = computed(() => {
        return getRedirectRoute()
    })

    /**
     * Forgot password
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function forgot(values = { email: '' }) {
        // Email typed in the form
        user.value = { ...values }
        // Call to the api
        return ApiService.post('password/email', values)
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Code to validate forgotten password
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function forgotCode(values = { email: '', code: '' }) {
        // Code typed in the form
        user.value = { ...values }
        return ApiService.post('password/code/verify', values)
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Reset the password
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function forgotReset(values = { email: '', code: '', password: '' }) {
        return ApiService.post('password/reset', values)
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }

    /**
     * Reset the password
     * @param values
     * @returns {Promise<axios.AxiosResponse>}
     */
    async function createNewPassword(userId, values = { code: '', password: '' }) {
        return ApiService.post(`password/reset/user/${userId}`, values)
            .then(({ data }) => {
                return data
            })
            .catch(({ data }) => {
                throw data.message
            })
    }


    /**
     * Redirect authenticated
     */
    function getRedirectRoute() {
        return 'admin-dashboard'
        // switch (role.value) {
        //     case RolesHelper.Roles.User:
        //         return 'user-dashboard'
        //     case RolesHelper.Roles.Editor:
        //         return 'editor-dashboard'
        //     case RolesHelper.Roles.Publisher:
        //         return 'publisher-dashboard'
        //     case RolesHelper.Roles.Manager:
        //         return 'admin-dashboard'
        //     case RolesHelper.Roles.Moderator:
        //         return 'admin-dashboard'
        //     case RolesHelper.Roles.Admin:
        //         return 'admin-dashboard'
        // }
    }

    return {
        user,
        errors,
        isVerified,
        isAuthenticated,
        role,
        roles,
        redirectionRoute,

        purgeAuth,
        login,
        logout,
        me,
        setAuth,

        register,
        registerCode,
        registerPassword,

        forgot,
        forgotCode,
        forgotReset,

        createNewPassword,

        getRedirectRoute,
    }
})
